import React, { useEffect, useId, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, MapPin, X } from 'lucide-react';
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  Handle,
  MiniMap,
  Position,
  ReactFlowProvider,
  useReactFlow,
  type NodeProps,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { BILLS_PAYABLES_HANDBOOK_HREF } from '../../utils/billsPayablesPolicy';
import {
  BILL_FLOW_NODE_TYPE,
  buildBillPayFlowGraph,
  type BillFlowActor,
  type BillFlowNodeData,
} from '../../utils/billPayFlowLayout';
import '../board/InspirationBoard.css';
import './BillPayFlowPanel.css';

/**
 * Bill-pay decision tree on the same React Flow board used by
 * OrgChartBoard / InspirationBoard (pan, zoom, fitView, dotted background).
 */

function ActorBadge({ kind, label }: BillFlowActor) {
  return (
    <span className={`bp-flow-actor bp-flow-actor--${kind}`}>{label}</span>
  );
}

function LocationBadge({ label }: { label: string }) {
  return (
    <span className="bp-flow-loc">
      <MapPin size={10} aria-hidden className="bp-flow-loc-pin" />
      {label}
    </span>
  );
}

function BillFlowCard({
  data,
  id,
  sourcePosition = Position.Bottom,
  targetPosition = Position.Top,
}: NodeProps<BillFlowNodeData>) {
  const tone = data.tone ?? 'plain';
  /** Returned PR node needs a top source so the resubmit loop climbs to Request + quote. */
  const loopSource = id === 'po-returned';
  return (
    <div className={`bp-flow-card bp-flow-card--${tone}`}>
      <Handle type="target" position={targetPosition} className="bp-flow-handle" id="in" />
      <div className="bp-flow-card-actors">
        {data.actors.map((a) => (
          <ActorBadge key={`${a.kind}-${a.label}`} {...a} />
        ))}
      </div>
      <div className="bp-flow-card-title">
        {data.title.map((line) => (
          <span key={line} className="bp-flow-card-title-line">
            {line}
          </span>
        ))}
      </div>
      {data.sub && data.sub.length > 0 && (
        <div className="bp-flow-card-sub">
          {data.sub.map((line) => (
            <span key={line} className="bp-flow-card-sub-line">
              {line}
            </span>
          ))}
        </div>
      )}
      {data.location && <LocationBadge label={data.location} />}
      <Handle type="source" position={sourcePosition} className="bp-flow-handle" id="out" />
      {loopSource && (
        <Handle type="source" position={Position.Left} className="bp-flow-handle" id="loop" />
      )}
    </div>
  );
}

const nodeTypes = { [BILL_FLOW_NODE_TYPE]: BillFlowCard };

function BillPayFlowBoardInner({ open }: { open: boolean }) {
  const rf = useReactFlow();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const didFit = useRef(false);
  const paneReady = useRef(false);

  const graph = useMemo(() => buildBillPayFlowGraph(), []);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey) event.preventDefault();
    };
    const onGesture = (event: Event) => event.preventDefault();
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('gesturestart', onGesture);
    el.addEventListener('gesturechange', onGesture);
    el.addEventListener('gestureend', onGesture);
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('gesturestart', onGesture);
      el.removeEventListener('gesturechange', onGesture);
      el.removeEventListener('gestureend', onGesture);
    };
  }, []);

  useEffect(() => {
    if (!open) {
      didFit.current = false;
      paneReady.current = false;
      return;
    }
    const el = wrapperRef.current;
    if (!el || graph.nodes.length === 0) return;

    const zoomParam =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('zoom')
        : null;

    const applyFit = (force = false) => {
      const { width, height } = el.getBoundingClientRect();
      const paneH = el.querySelector('.react-flow__pane')?.getBoundingClientRect().height || 0;
      if (width < 8 || height < 8 || paneH < 8) return false;
      if (!force && didFit.current && paneReady.current) return true;
      didFit.current = true;
      paneReady.current = true;
      if (zoomParam === 'po') {
        // Focus the decide fork (Reject / Send back / Approve) + return loop.
        const focus = graph.nodes.filter((n) =>
          ['po-quote', 'po-bands', 'po-decide', 'po-reject', 'po-returned', 'po-issued', 'po-pay'].includes(
            n.id,
          ),
        );
        if (focus.length > 0) {
          rf.fitView({ nodes: focus, padding: 0.28, duration: 0, maxZoom: 1.05 });
        }
        return true;
      }
      rf.fitView({ padding: 0.12, duration: 0, minZoom: 0.05, maxZoom: 1.2 });
      return true;
    };
    // Panel may open after layout; retry until the pane has real size.
    const t0 = window.setTimeout(() => applyFit(true), 50);
    const t1 = window.setTimeout(() => applyFit(true), 250);
    const t2 = window.setTimeout(() => applyFit(true), 600);
    const observer = new ResizeObserver(() => applyFit(false));
    observer.observe(el);
    const flow = el.querySelector('.react-flow');
    if (flow) observer.observe(flow);
    return () => {
      window.clearTimeout(t0);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      observer.disconnect();
    };
  }, [open, graph.nodes, rf]);

  return (
    <div
      ref={wrapperRef}
      className="inspiration-board bp-flow-board"
      role="img"
      aria-label="Bill payment decision flow"
    >
      <ReactFlow
        nodes={graph.nodes}
        edges={graph.edges}
        nodeTypes={nodeTypes}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnDrag
        panOnScroll
        preventScrolling
        zoomOnDoubleClick={false}
        minZoom={0.08}
        maxZoom={2.5}
        proOptions={{ hideAttribution: true }}
        className="bp-flow-rf"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
        defaultEdgeOptions={{ type: 'smoothstep' }}
      >
        <Background variant={BackgroundVariant.Dots} gap={22} size={1.4} color="#d8dee9" />
        <Controls showInteractive={false} />
        <MiniMap maskColor="rgba(244,246,250,0.7)" pannable zoomable />
      </ReactFlow>
    </div>
  );
}

function BillPayFlowBoard({ open }: { open: boolean }) {
  return (
    <ReactFlowProvider>
      <BillPayFlowBoardInner open={open} />
    </ReactFlowProvider>
  );
}

interface BillPayFlowPanelProps {
  open: boolean;
  onClose: () => void;
}

const BillPayFlowPanel: React.FC<BillPayFlowPanelProps> = ({ open, onClose }) => {
  const titleId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="bp-flow-modal-backdrop" onClick={onClose}>
      <div
        className="bp-flow-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="bp-flow-modal-head">
          <h2 id={titleId} className="bp-flow-modal-title">
            How a bill gets paid
          </h2>
          <button type="button" className="bp-flow-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="bp-flow-modal-body">
        <p className="bp-flow-panel-lead">
          <span className="bp-flow-panel-lead-text">
            One decision tree for every bill path the CRM supports today. Full rules:
          </span>{' '}
          <Link to={BILLS_PAYABLES_HANDBOOK_HREF} className="bp-flow-panel-policy-link">
            <BookOpen size={13} strokeWidth={2} aria-hidden className="bp-flow-panel-policy-icon" />
            Bills &amp; Payables policy
          </Link>
        </p>

        <div className="bp-flow-legend" aria-label="Badge legend">
          <span className="bp-flow-legend-item bp-flow-legend-item--person">
            <span className="bp-flow-legend-swatch" /> Person (employee)
          </span>
          <span className="bp-flow-legend-item bp-flow-legend-item--agent">
            <span className="bp-flow-legend-swatch" /> Agent
          </span>
          <span className="bp-flow-legend-item bp-flow-legend-item--system">
            <span className="bp-flow-legend-swatch" /> System
          </span>
          <span className="bp-flow-legend-item">
            <span className="bp-flow-legend-swatch bp-flow-legend-swatch--loc" /> Where in the CRM
          </span>
        </div>

        <BillPayFlowBoard open={open} />

        <ol className="bp-flow-mobile-steps">
          <li>
            <strong>What is being bought?</strong> — PO purchase, timber package, CNC package, designer
            invoice, or engineering invoice (same review queue).
          </li>
          <li>
            <strong>PO purchase</strong> — Employee, PM, Hugh, or Caesar starts a PR → quote → approval
            by dollar band → approve (PO), send back (<code>returned</code>), or END Rejected → payment
            method → END PO purchased.
          </li>
          <li>
            <strong>Timber / CNC</strong> — list → RFQ → supplier quote → award (B&amp;P bill) or END
            Rejected → PM/exec marks ready to pay (Juliana notified) → Cloud Functions / CRM creates QB
            bill for the approved amount only (due date from vendor terms, idempotent) → Juliana pays →
            END paid (no PO).
          </li>
          <li>
            <strong>Designer / Engineering</strong> — submit / CRM agent / Caesar enqueue → job-cost →
            staff DoA approve (PO) or END Rejected → ACH (QB bill + Juliana pays) or Project Card under
            $2k (Caesar charges) → END.
          </li>
          <li>
            <strong>Who does what</strong> — Automation runs in Cloud Functions / the CRM. CRM agent
            handles API-driven steps. Hugh handles purchasing and PO closing. CFO bot handles finance
            analysis / forecast checks. Juliana (humans) approve ready-to-pay and execute payment in
            QuickBooks (Matthew may also pay). Agents never pay QuickBooks bills (Caesar may charge
            Project Card under $2k).
          </li>
        </ol>

        <div className="bp-flow-notes" aria-label="Flow notes">
          <p>
            Who pays in QuickBooks: Juliana or Matthew only. Agents never pay QuickBooks bills. Caesar may
            charge the Project Card for designer invoices under $2,000 after staff approval. Ready-to-pay
            by a PM or exec notifies Juliana and the CRM creates a QuickBooks bill for only the approved
            amount (portion note, vendor-terms due date, idempotent).
          </p>
          <p>
            DoA bands from DEFAULT_PURCHASING_POLICY in src/utils/purchasingPolicy.ts (job gates). Live
            Firestore purchasingPolicy may override. COO self-authorize ≤ $500 (purchasingApprovals.ts).
          </p>
          <p>
            PR send-back uses status <code>returned</code> (
            <code>returnPurchaseRequisition</code> in purchasingRequisitions.ts) and loops to Request +
            quote for edit/resubmit. Rejected PR/quote/invoice stops at END Rejected. Designer /
            engineering invoices have approve | reject only (no return status). RFQ loser reject stamps
            the quote; package stays open for other quotes — no coded “re-open RFQ” status.
          </p>
          <p>
            The CRM agent has no invoices@ mailbox poller today; enqueueDesignerInvoice is used by the
            CRM agent skill and Caesar. ACH POs are marked purchased when their linked QuickBooks bill
            is paid (<code>supplierBillQboPaid</code>), not by a sweep of every non-void bill. CFO bot
            may read AP for forecast checks only.
          </p>
        </div>

        <p className="bp-flow-panel-foot">
          See the{' '}
          <Link to={BILLS_PAYABLES_HANDBOOK_HREF}>Bills &amp; Payables policy</Link> for owners per
          step and open decisions.
        </p>
        </div>
      </div>
    </div>
  );
};

export default BillPayFlowPanel;
